import Vue from 'vue'
import JsonApiObject from '@/_models/JsonApi'

import { query } from '@/_type/vbit-api'
import store from '@/store'
import LogProjectDialog from '@/views/Project/Project/dialogs/logProject/logProject.vue'
import ProjectClose from '@/views/Project/Project/dialogs/projectClose/projectClose.vue'
import ModulePost from '@/views/Project/Project/dialogs/modulePost/modulePost.vue'
import PostModuleDialog from '@/views/Project/Settings/Modules/dialogs/postModule.vue'
import Log from '@/views/Log/models/log'
import CompanyModel from '@/views/Company/models/company'
import ProjectModuleModel from '@/views/Project/Project/models/projectModule'

export default class ProjectModel extends JsonApiObject {
    static async post(data: any) {
        let query: query = {
            body: { data: data },
        }

        let res = await Vue.prototype.$vbit.post('project', `projects`, query).catch((error) => {
            return this.handleProjectError(error)
        })

        return res.data ? new ProjectModel(res.data, res.included) : res
    }

    static async search(query) {
        let res = await Vue.prototype.$vbit.get('project', 'projects', query)

        let projects = res.data.map((project) => new ProjectModel(project, res.included))

        return projects
    }

    public constructor(data: any, included?) {
        super(data, included)
    }

    customId
    bklrId
    name
    timeZone
    description
    avatarFileId
    address
    currency
    latitude
    longitude
    emergencyNumber
    agreementUrl
    hseBoardDescription
    publicHseBoardDescription
    hasHseContacts
    hasHseDocuments
    createdAt
    updatedAt
    permissions
    canCreateBklrProject

    get modules() {
        return this._relationships['modules'] ? this._relationships['modules'] : []
    }

    set modules(modules: Array<ProjectModuleModel>) {
        this._relationships['modules'] = modules
    }

    getModule(moduleId: string) {
        return this.modules.find((module) => module.module && module.module._id == moduleId)
    }

    hasModule(moduleId: string) {
        return this.modules.findIndex((module) => module.module && module.module._id == moduleId) >
            -1
            ? true
            : false
    }

    get hasOwner() {
        return this._relationships['owner'] ? true : false
    }

    get owner() {
        return this.hasOwner ? this._relationships['owner'] : null
    }

    get ownerName() {
        return this.hasOwner ? this.owner.name : null
    }

    get hasUserSettings() {
        return this._relationships['userSettings'] ? true : false
    }

    get userSettings() {
        return this.hasUserSettings ? this._relationships['userSettings'] : null
    }

    async get(include) {
        let query = {
            include,
        }

        let res = await Vue.prototype.$vbit.get('project', `projects/${this._id}`, query)

        return res.data ? this.initalize(res.data, res.included) : res
    }

    async initPostModule(moduleId: string, project: ProjectModel) {
        return await JsonApiObject.openDialog(PostModuleDialog, { moduleId, project })
    }

    async postModule(moduleId: string, project: ProjectModel, filters?: Record<string, any>) {
        let query = {
            filters,
            body: {
                data: {
                    type: 'projectModule',
                    relationships: {
                        project: {
                            data: {
                                type: project._type,
                                id: project._id,
                            },
                        },
                        module: {
                            data: {
                                type: 'module',
                                id: moduleId,
                            },
                        },
                    },
                },
            },
        }

        let res = await Vue.prototype.$vbit.post('project', `projectModules`, query)
        if (res.data) {
            let projectModule = new ProjectModuleModel(res.data, res.included)
            this.modules = [...this.modules, projectModule]

            this.updatedAt = projectModule.updatedAt

            // Temporary solution to update modules
            store.commit('projectModule', res.data)
            return projectModule
        } else {
            return res
        }
    }

    async createBklrProject(projectId: string) {
        return await Vue.prototype.$vbit.post(
            'project',
            `projects/${projectId}/createBklrProject`,
            { updatedAt: this['updatedAt'] }
        )
    }

    static handleProjectError(error) {
        if (error.code) store.commit('closeSnackbar')

        return {
            type: 'error',
            code: error.code ? error.code : null,
            text: error.code ? `fe_project.error_${error.code}` : null,
        }
    }

    static async showLog(project) {
        await JsonApiObject.openDialog(LogProjectDialog, { project: project })
    }

    static async getLog(projectId: string, timeStamp: any) {
        let data = {
            'filter[activityLogEntityId]': projectId,
            'filter[activityLogNumber]': ['1'],
            'filter[time][lt]': timeStamp,
        }

        let res = await Log.getLog(data)

        return res
    }

    async initClose(project: ProjectModel) {
        return await JsonApiObject.openDialog(ProjectClose, { project: project })
    }

    async getUpdatedAt() {
        let res = await Vue.prototype.$vbit.get('project', `projects/${this._id}`)
        this.updatedAt = res.data.attributes.updatedAt
        return this.updatedAt
    }

    async patch(data: any) {
        let query = {
            updatedAt: this['updatedAt'],
            include: ['modules'],
            body: { data: data },
        }

        let res = await Vue.prototype.$vbit.patch('project', `projects/${this._id}`, query)
        this.initalize(res.data, res.included)
        return res.data ? this : res
    }
}
